import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import api, { photoUrl } from "./api";
import { AuthProvider, useAuth } from "./auth";
import JapanMap from "./JapanMap";
import AdminPanel from "./AdminPanel";
import Signatures from "./Signatures";
import { MediaItem, MediaUploadButton } from "./Media";
import HighlightReel from "./HighlightReel";
import {
  ContentProvider,
  useContent,
  EditableText,
  EditableList,
  ReorderControls,
} from "./content";
import { GOLD, BG, tf, ts } from "./data";
import "./japan.css";

// ---------- Nav ----------
function Nav({ view, setView, photoCount, isAdmin, onLogin, onLogout, onAdmin }) {
  const [open, setOpen] = useState(false);
  const items = [
    { id: "home", l: "Home" },
    { id: "journey", l: "Journey" },
    { id: "gallery", l: `Photos${photoCount ? ` (${photoCount})` : ""}` },
    { id: "map", l: "Map" },
    { id: "signatures", l: "Signatures" },
  ];
  return (
    <nav className="jp-nav" data-testid="top-nav">
      <div className="jp-nav-brand" onClick={() => setView("home")} data-testid="brand-home">
        <span style={{ fontSize: "1.35rem" }}>🇯🇵</span>
        <span style={{ ...ts, fontSize: "0.95rem", fontWeight: 700, letterSpacing: "0.01em" }}>
          Shah Family · <span style={{ color: GOLD }}>Japan 2026</span>
        </span>
      </div>

      <div className="jp-nav-links">
        {items.map((n) => (
          <button
            key={n.id}
            data-testid={`nav-${n.id}`}
            onClick={() => setView(n.id)}
            className={`jp-nav-btn ${view === n.id ? "active" : ""}`}
          >
            {n.l}
          </button>
        ))}
        {isAdmin ? (
          <>
            <button data-testid="nav-admin" onClick={onAdmin} className="jp-nav-btn admin">
              Admin
            </button>
            <button data-testid="nav-logout" onClick={onLogout} className="jp-nav-btn ghost">
              Logout
            </button>
          </>
        ) : (
          <button data-testid="nav-login" onClick={onLogin} className="jp-nav-btn ghost">
            Sign in
          </button>
        )}
      </div>

      <button className="jp-burger" onClick={() => setOpen((o) => !o)} aria-label="menu" data-testid="burger">
        <span /><span /><span />
      </button>

      {open && (
        <div className="jp-mobile-menu" data-testid="mobile-menu">
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => { setView(n.id); setOpen(false); }}
              className={`jp-nav-btn ${view === n.id ? "active" : ""}`}
            >
              {n.l}
            </button>
          ))}
          {isAdmin ? (
            <>
              <button onClick={() => { onAdmin(); setOpen(false); }} className="jp-nav-btn admin">Admin</button>
              <button onClick={() => { onLogout(); setOpen(false); }} className="jp-nav-btn ghost">Logout</button>
            </>
          ) : (
            <button onClick={() => { onLogin(); setOpen(false); }} className="jp-nav-btn ghost">Sign in</button>
          )}
        </div>
      )}
    </nav>
  );
}

// ---------- Login modal ----------
function LoginModal({ onClose, onSuccess }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      onSuccess?.();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="jp-modal-bg" onClick={onClose} data-testid="login-modal">
      <div className="jp-modal" onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: "1.8rem", marginBottom: "0.4rem" }}>🔐</div>
        <h3 style={{ ...ts, fontSize: "1.4rem", marginBottom: "0.3rem" }}>Admin Sign-in</h3>
        <p style={{ ...tf, color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", marginBottom: "1.4rem" }}>
          Family admins only — uploads & analytics
        </p>
        <form onSubmit={submit}>
          <input data-testid="login-email" type="email" placeholder="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required autoFocus className="jp-input" />
          <input data-testid="login-password" type="password" placeholder="password" value={password}
            onChange={(e) => setPassword(e.target.value)} required className="jp-input" />
          {err && <div className="jp-err" data-testid="login-error">{err}</div>}
          <button data-testid="login-submit" type="submit" className="jp-btn-primary" disabled={busy}>
            {busy ? "Signing in..." : "Sign in"}
          </button>
        </form>
        <button onClick={onClose} className="jp-btn-ghost" data-testid="login-cancel">Cancel</button>
      </div>
    </div>
  );
}

// ---------- Home ----------
function Home({ setView }) {
  const { content } = useContent();
  const { hero, stats, cities } = content;

  return (
    <div className="jp-home" data-testid="home-view">
      <HighlightReel />
      <div className="jp-home-bg" />
      <div className="jp-home-inner">
        <EditableText
          path={["hero", "eyebrow"]}
          value={hero.eyebrow}
          className="jp-pill"
          as="div"
          testId="home-pill"
        />

        <h1 className="jp-hero-title" data-testid="hero-title">
          <EditableText
            path={["hero", "line1"]}
            value={hero.line1}
            as="span"
            style={{ display: "block", fontWeight: 300, color: "rgba(255,255,255,0.85)" }}
            testId="hero-line1"
          />
          <span style={{ whiteSpace: "nowrap" }}>
            <EditableText
              path={["hero", "line2a"]}
              value={hero.line2a}
              as="span"
              style={{ color: GOLD, fontStyle: "italic" }}
              testId="hero-line2a"
            />
            {" "}
            <EditableText
              path={["hero", "line2b"]}
              value={hero.line2b}
              as="span"
              style={{ color: "#fff" }}
              testId="hero-line2b"
            />
          </span>
        </h1>

        <p className="jp-hero-sub">
          <EditableText
            path={["hero", "subtitle"]}
            value={hero.subtitle}
            as="span"
            testId="hero-subtitle"
          />
          <br />
          <EditableText
            path={["hero", "subtitle_extra"]}
            value={hero.subtitle_extra}
            as="span"
            style={{ color: "rgba(255,255,255,0.3)" }}
            testId="hero-subtitle-extra"
          />
        </p>

        <div className="jp-stats" data-testid="hero-stats">
          {stats.map((s, i) => (
            <div
              key={`${s.label}-${i}`}
              className="jp-stat"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`stat-${i}`}
            >
              <div className="jp-stat-emoji">{s.emoji}</div>
              <EditableText
                path={["stats", i, "value"]}
                value={s.value}
                className="jp-stat-val"
                as="div"
                testId={`stat-${i}-value`}
              />
              <EditableText
                path={["stats", i, "label"]}
                value={s.label}
                className="jp-stat-lbl"
                as="div"
                testId={`stat-${i}-label`}
              />
            </div>
          ))}
        </div>

        <div className="jp-cities" data-testid="city-cards">
          {cities.map((c, i) => (
            <div
              key={`${c.name}-${i}`}
              className="jp-city"
              style={{
                background: `linear-gradient(135deg, ${c.color}55 0%, ${c.color}22 100%)`,
                borderColor: `${c.color}66`,
                animationDelay: `${i * 90}ms`,
              }}
              onClick={() => setView("journey")}
              data-testid={`city-card-${c.name.toLowerCase()}`}
            >
              <ReorderControls section="cities" index={i} total={cities.length} label={c.name} />
              <div className="jp-city-emoji">{c.emoji}</div>
              <EditableText
                path={["cities", i, "name"]}
                value={c.name}
                className="jp-city-name"
                as="div"
                testId={`city-${i}-name`}
              />
              <EditableText
                path={["cities", i, "nights"]}
                value={c.nights}
                className="jp-city-nights"
                as="div"
                testId={`city-${i}-nights`}
              />
              <EditableText
                path={["cities", i, "hotel"]}
                value={c.hotel}
                className="jp-city-hotel"
                as="div"
                testId={`city-${i}-hotel`}
              />
            </div>
          ))}
        </div>

        <button onClick={() => setView("journey")} className="jp-cta" data-testid="cta-explore">
          <span>{hero.cta}</span>
          <span style={{ marginLeft: "0.6rem", display: "inline-block", transition: "transform 0.2s" }}>→</span>
        </button>
      </div>
    </div>
  );
}

// ---------- Journey list ----------
function Journey({ openDay, photosByKey }) {
  const { content, isAdmin, updateField } = useContent();
  const days = content.days;

  const addDay = (position) => {
    const dayNum = position === "start" ? 1 : (days[days.length - 1]?.day || 0) + 1;
    const template = {
      day: dayNum,
      date: "edit me",
      city: "City",
      hotel: "Hotel",
      emoji: "✨",
      color: "#3B2A4A",
      title: "New day — click to edit",
      description: "Add description here.",
      highlights: [],
    };
    let next;
    if (position === "start") {
      // Insert at start, renumber subsequent days
      next = [template, ...days.map((d) => ({ ...d, day: d.day + 1 }))];
    } else {
      next = [...days, template];
    }
    updateField(["days"], next);
  };

  return (
    <div className="jp-page" data-testid="journey-view">
      <div className="jp-page-inner narrow">
        <h2 className="jp-h2">Day by Day</h2>
        <p className="jp-h2-sub">{days.length} days · {content.cities.length} cities · countless memories</p>

        {isAdmin && (
          <div className="jp-day-admin-bar" data-testid="day-admin-bar">
            <button onClick={() => addDay("start")} className="jp-add-btn" data-testid="add-day-start">
              + Insert day before Day 1
            </button>
            <button onClick={() => addDay("end")} className="jp-add-btn" data-testid="add-day-end">
              + Add day at the end
            </button>
          </div>
        )}

        <div className="jp-timeline">
          <div className="jp-timeline-line" />
          {days.map((day, i) => {
            const dayPhotos = photosByKey[`d${day.day}`] || [];
            return (
              <div key={day.day} className="jp-timeline-item" style={{ animationDelay: `${i * 50}ms` }}>
                <div
                  className="jp-timeline-dot"
                  style={{ background: day.color, boxShadow: `0 0 18px ${day.color}99` }}
                >
                  {day.emoji}
                </div>
                <div className="jp-timeline-card" style={{ "--accent": day.color }}>
                  <ReorderControls section="days" index={i} total={days.length} label={`Day ${day.day}`} />
                  <div
                    onClick={() => !isAdmin && openDay(day)}
                    style={{ cursor: isAdmin ? "default" : "pointer" }}
                    data-testid={`day-card-${day.day}`}
                  >
                    <div className="jp-tc-head">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="jp-tc-meta">
                          Day {day.day} ·{" "}
                          <EditableText
                            path={["days", i, "date"]}
                            value={day.date}
                            as="span"
                            testId={`day-${day.day}-date`}
                          />
                        </div>
                        <EditableText
                          path={["days", i, "title"]}
                          value={day.title}
                          className="jp-tc-title"
                          as="div"
                          testId={`day-${day.day}-title`}
                        />
                      </div>
                      <EditableText
                        path={["days", i, "city"]}
                        value={day.city}
                        className="jp-tc-tag"
                        as="div"
                        style={{ background: `${day.color}44`, borderColor: `${day.color}66` }}
                        testId={`day-${day.day}-city`}
                      />
                    </div>
                    <p className="jp-tc-desc">
                      {(day.description || "").substring(0, 130)}…
                    </p>
                    <div className="jp-tc-tags">
                      {(day.highlights || []).slice(0, 3).map((h, j) => (
                        <span key={`${j}-${h}`} className="jp-tag">✦ {h}</span>
                      ))}
                      {(day.highlights || []).length > 3 && (
                        <span className="jp-tag gold">+{day.highlights.length - 3}</span>
                      )}
                    </div>
                    {dayPhotos.length > 0 && (
                      <div className="jp-tc-thumbs">
                        {dayPhotos.slice(0, 5).map((p) => (
                          <img key={p.id} src={photoUrl(p.url)} alt="" />
                        ))}
                        {dayPhotos.length > 5 && (
                          <div className="jp-tc-more">+{dayPhotos.length - 5}</div>
                        )}
                      </div>
                    )}
                    {isAdmin && (
                      <button
                        className="jp-open-day-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDay(day);
                        }}
                        data-testid={`open-day-${day.day}`}
                      >
                        Open day →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- Day detail ----------
function DayDetail({ dayNumber, back, media, onUpdateMedia, onDelete, onReload, isAdmin }) {
  const { content } = useContent();
  const idx = content.days.findIndex((d) => d.day === dayNumber);
  const day = content.days[idx];

  // Split media: linked to a specific highlight vs general
  const linkedByHighlight = useMemo(() => {
    const m = {};
    for (const item of media) {
      if (item.highlight_index != null) {
        (m[item.highlight_index] = m[item.highlight_index] || []).push(item);
      }
    }
    return m;
  }, [media]);
  const unlinked = useMemo(
    () => media.filter((m) => m.highlight_index == null),
    [media]
  );

  if (!day) {
    return (
      <div className="jp-page">
        <div className="jp-page-inner">
          <button onClick={back} className="jp-back">← Back to Journey</button>
          <p style={{ ...tf, color: "rgba(255,255,255,0.5)" }}>Day not found.</p>
        </div>
      </div>
    );
  }

  const highlightOptions = (day.highlights || []).map((h, i) => ({ idx: i, text: h }));

  return (
    <div className="jp-page" data-testid={`day-detail-${day.day}`}>
      <div className="jp-page-inner narrowest">
        <button onClick={back} className="jp-back" data-testid="back-to-journey">← Back to Journey</button>

        <div
          className="jp-day-hero"
          style={{
            background: `linear-gradient(135deg, ${day.color}66, ${day.color}33)`,
            borderColor: `${day.color}77`,
          }}
        >
          <div style={{ fontSize: "3rem", marginBottom: "0.8rem" }}>{day.emoji}</div>
          <div className="jp-day-meta">
            Day {day.day} ·{" "}
            <EditableText path={["days", idx, "date"]} value={day.date} as="span" testId={`detail-${day.day}-date`} />{" "}
            ·{" "}
            <EditableText path={["days", idx, "city"]} value={day.city} as="span" testId={`detail-${day.day}-city`} />
          </div>
          <EditableText
            path={["days", idx, "title"]}
            value={day.title}
            as="h2"
            className="jp-day-title"
            testId={`detail-${day.day}-title`}
          />
          <EditableText
            path={["days", idx, "description"]}
            value={day.description}
            multiline
            as="p"
            className="jp-day-desc"
            testId={`detail-${day.day}-desc`}
          />
          <div className="jp-day-hotel">
            🏨{" "}
            <EditableText path={["days", idx, "hotel"]} value={day.hotel} as="span" testId={`detail-${day.day}-hotel`} />
          </div>
        </div>

        <div className="jp-section">
          <h3 className="jp-h3">Highlights</h3>
          <div className="jp-hl-list">
            {(day.highlights || []).map((h, i) => {
              const items = linkedByHighlight[i] || [];
              return (
                <div key={`${i}-${h}`} className="jp-hl-block" data-testid={`hl-block-${day.day}-${i}`}>
                  <div className="jp-hl-item">
                    <span style={{ color: day.color, fontSize: "1.1rem" }}>✦</span>
                    {isAdmin ? (
                      <EditableText
                        path={["days", idx, "highlights", i]}
                        value={h}
                        as="span"
                        className="jp-list-text"
                        testId={`highlight-${day.day}-${i}`}
                      />
                    ) : (
                      <span>{h}</span>
                    )}
                  </div>
                  {items.length > 0 && (
                    <div className="jp-hl-media-row" data-testid={`hl-media-${day.day}-${i}`}>
                      {items.map((m) => (
                        <MediaItem
                          key={m.id}
                          item={m}
                          isAdmin={isAdmin}
                          day={day}
                          onUpdate={onUpdateMedia}
                          onDelete={onDelete}
                          size="thumb"
                          showHighlightBadge={false}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {isAdmin && (
              <details className="jp-hl-manage" data-testid={`hl-manage-${day.day}`}>
                <summary>+ Add / delete / reorder highlights</summary>
                <div style={{ marginTop: "0.8rem" }}>
                  <EditableList
                    path={["days", idx, "highlights"]}
                    items={day.highlights || []}
                    placeholder="highlight"
                    testId={`highlights-edit-${day.day}`}
                  />
                </div>
              </details>
            )}
          </div>
        </div>

        <div className="jp-photos-card">
          <div style={{ fontSize: "2rem", marginBottom: "0.4rem" }}>📷</div>
          <h4 style={{ ...ts, fontSize: "1.05rem", marginBottom: "0.25rem" }}>
            Photos &amp; videos from Day {day.day}
          </h4>
          <p className="jp-photos-sub">
            {media.length} item{media.length === 1 ? "" : "s"}
            {isAdmin ? " · click any caption to edit, dropdown to link to a highlight" : ""}
          </p>

          {isAdmin && (
            <MediaUploadButton
              dayKey={`d${day.day}`}
              highlightOptions={highlightOptions}
              onUploaded={onReload}
              testId={`upload-day-${day.day}`}
              label="Upload photos / videos"
            />
          )}

          {unlinked.length > 0 && (
            <div className="jp-day-grid" data-testid={`day-${day.day}-grid`}>
              {unlinked.map((m) => (
                <MediaItem
                  key={m.id}
                  item={m}
                  isAdmin={isAdmin}
                  day={day}
                  onUpdate={onUpdateMedia}
                  onDelete={onDelete}
                  size="thumb"
                />
              ))}
            </div>
          )}

          {media.length === 0 && !isAdmin && (
            <div style={{ ...tf, color: "rgba(255,255,255,0.3)", fontSize: "0.78rem", marginTop: "0.8rem" }}>
              No photos yet — check back soon!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Gallery ----------
function Gallery({ media, isAdmin, onUpdate, onDelete, onReload, openDay }) {
  const { content } = useContent();
  return (
    <div className="jp-page" data-testid="gallery-view">
      <div className="jp-page-inner">
        <h2 className="jp-h2">Photos &amp; Videos</h2>
        <p className="jp-h2-sub">
          {media.length} item{media.length === 1 ? "" : "s"} from the journey
        </p>

        {isAdmin && (
          <div className="jp-upload-strip">
            <p>Upload general items here (no day link), or open a day to upload day-specific photos / videos with a highlight link.</p>
            <MediaUploadButton
              dayKey="general"
              onUploaded={onReload}
              testId="upload-gallery"
              label="Upload photos / videos"
            />
          </div>
        )}

        {media.length > 0 ? (
          <div className="jp-gallery-masonry">
            {media.map((m) => {
              const d = content.days.find((x) => `d${x.day}` === m.day_key);
              const isVideo = (m.type === "video") || (m.mime || "").startsWith("video/");
              return (
                <div key={m.id} className="jp-gal-item" data-testid={`gallery-item-${m.id}`}>
                  {isVideo ? (
                    <video
                      src={photoUrl(m.url)}
                      controls
                      preload="metadata"
                      playsInline
                      data-testid={`gallery-video-${m.id}`}
                    />
                  ) : (
                    <img src={photoUrl(m.url)} alt={m.caption || ""} />
                  )}
                  {isVideo && <span className="jp-media-type-badge">▶</span>}
                  {m.caption && (
                    <div className="jp-gal-caption" data-testid={`gallery-caption-${m.id}`}>
                      {m.caption}
                    </div>
                  )}
                  <div className="jp-gal-overlay">
                    {d && (
                      <button className="jp-gal-day" onClick={() => openDay(d)}
                        data-testid={`gallery-day-link-${d.day}`}>
                        Day {d.day} · {(d.title || "").split(" — ")[0]}
                      </button>
                    )}
                    {isAdmin && (
                      <button className="jp-del" onClick={() => onDelete(m.id)}
                        data-testid={`gallery-delete-${m.id}`}>×</button>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="jp-gal-meta">
                      <GalleryCaptionEdit item={m} onUpdate={onUpdate} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "5rem 1rem" }}>
            <div style={{ fontSize: "3.5rem", marginBottom: "1rem" }}>📸</div>
            <p style={{ ...tf, color: "rgba(255,255,255,0.3)" }}>
              No photos yet — {isAdmin ? "upload some above" : "check back soon"}!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function GalleryCaptionEdit({ item, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.caption || "");
  useEffect(() => { if (!editing) setDraft(item.caption || ""); }, [item.caption, editing]);
  const commit = () => {
    const next = draft.trim();
    if (next !== (item.caption || "")) onUpdate(item.id, { caption: next });
    setEditing(false);
  };
  if (editing) {
    return (
      <input
        autoFocus className="jp-edit-input" value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        placeholder="Add a caption…"
        data-testid={`gallery-caption-input-${item.id}`}
      />
    );
  }
  return (
    <button
      className="jp-media-caption-btn"
      onClick={() => setEditing(true)}
      data-testid={`gallery-caption-btn-${item.id}`}
      title="Click to edit caption"
    >
      {item.caption || <span style={{ opacity: 0.5, fontStyle: "italic" }}>Add caption…</span>}
      <span className="jp-edit-pencil" aria-hidden>✎</span>
    </button>
  );
}

// ---------- Map view ----------
function MapView() {
  return (
    <div className="jp-page" data-testid="map-view">
      <div className="jp-page-inner">
        <h2 className="jp-h2">Journey Map</h2>
        <p className="jp-h2-sub">8 destinations · pinch to zoom · hover pins</p>
        <JapanMap />
        <p style={{ ...tf, color: "rgba(255,255,255,0.3)", fontSize: "0.72rem", textAlign: "center", marginTop: "1rem" }}>
          Map tiles: © OpenStreetMap · © CARTO
        </p>
      </div>
    </div>
  );
}

// ---------- Track helper ----------
function track(event, extra = {}) {
  api.post("/analytics/track", {
    event,
    path: window.location.pathname,
    referrer: document.referrer || null,
    ...extra,
  }).catch((err) => {
    if (process.env.NODE_ENV !== "production") {
      console.debug("analytics track failed:", event, err?.message);
    }
  });
}

// ---------- Main shell ----------
function Shell() {
  const { user, isAdmin, logout } = useAuth();
  const [view, setView] = useState("home");
  const [selDayNum, setSelDayNum] = useState(null);
  const [media, setMedia] = useState([]);
  const [showLogin, setShowLogin] = useState(false);
  const trackedOnce = useRef(false);

  const mediaByKey = useMemo(() => {
    const m = {};
    for (const item of media) (m[item.day_key] = m[item.day_key] || []).push(item);
    return m;
  }, [media]);

  const loadMedia = useCallback(async () => {
    try {
      const { data } = await api.get("/media");
      setMedia(data);
    } catch (err) {
      console.error("Failed to load media:", err?.response?.data?.detail || err.message);
    }
  }, []);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  useEffect(() => {
    if (!trackedOnce.current) {
      track("page_view");
      trackedOnce.current = true;
    }
  }, []);

  useEffect(() => {
    if (view === "map") track("map_view");
    if (view === "gallery") track("gallery_view");
  }, [view]);

  const openDay = (day) => {
    setSelDayNum(day.day);
    setView("dayDetail");
    track("day_open", { day: day.day });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleUpdate = async (id, patch) => {
    try {
      const { data } = await api.patch(`/media/${id}`, patch);
      setMedia((items) => items.map((x) => (x.id === id ? data : x)));
    } catch (err) {
      alert("Update failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/media/${id}`);
      setMedia((items) => items.filter((x) => x.id !== id));
    } catch (err) {
      alert("Delete failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  useEffect(() => {
    if (view !== "dayDetail") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [view]);

  return (
    <ContentProvider isAdmin={isAdmin}>
      <div style={{ background: BG, minHeight: "100vh" }}>
        <Nav
          view={view}
          setView={setView}
          photoCount={media.length}
          isAdmin={isAdmin}
          onLogin={() => setShowLogin(true)}
          onLogout={logout}
          onAdmin={() => setView("admin")}
        />

        {isAdmin && (
          <div className="jp-edit-banner" data-testid="edit-banner">
            ✎ Edit mode — click any text to edit, ▲▼ to reorder, × to delete items.
          </div>
        )}

        {view === "home" && <Home setView={setView} />}
        {view === "journey" && <Journey openDay={openDay} photosByKey={mediaByKey} />}
        {view === "dayDetail" && selDayNum != null && (
          <DayDetail
            dayNumber={selDayNum}
            back={() => setView("journey")}
            media={mediaByKey[`d${selDayNum}`] || []}
            onUpdateMedia={handleUpdate}
            onDelete={handleDelete}
            onReload={loadMedia}
            isAdmin={isAdmin}
          />
        )}
        {view === "gallery" && (
          <Gallery
            media={media}
            isAdmin={isAdmin}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onReload={loadMedia}
            openDay={openDay}
          />
        )}
        {view === "map" && <MapView />}
        {view === "signatures" && <Signatures />}
        {view === "admin" && isAdmin && <AdminPanel />}

        {showLogin && (
          <LoginModal onClose={() => setShowLogin(false)} onSuccess={() => setShowLogin(false)} />
        )}

        <SiteFooter />
      </div>
    </ContentProvider>
  );
}

function SiteFooter() {
  const { content } = useContent();
  const f = content.footer || {};
  return (
    <footer className="jp-footer" data-testid="footer">
      <div className="jp-footer-row">
        <span>🇯🇵</span>
        <EditableText path={["footer", "line"]} value={f.line} as="span" testId="footer-line" />
      </div>
      <div className="jp-footer-easter" data-testid="footer-easter">
        <EditableText path={["footer", "easter"]} value={f.easter} as="span" testId="footer-easter-text" />
        <span className="jp-footer-secret">
          {" "}
          <EditableText path={["footer", "secret"]} value={f.secret} as="span" testId="footer-secret" />
        </span>
      </div>
      <div className="jp-footer-meta">
        Map © OpenStreetMap & CARTO · {new Date().getFullYear()}
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}
